///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { orderBy } from "lodash";

import { graphTypesV2, lineGraphSeries } from "../../../helpers/types";
import AutoComplete from "../../form-controls/AutoComplete";
import getAnalyticsGraphOptions from "../../../helpers/getAnalyticsGraphOptions";
import mapLineGraphDetails from "../../../helpers/mapLineGraphDetails";
import LineGraph from "./LineGraph";
import { setAnalyticsGraphType } from "../../../slices/analyticsSlice";
import mapAggregatedPricingForecastDetails from "../../../helpers/mapAggregatedPricingForecastDetails";
import mapCpmFrequencyGraphDetails from "../../../helpers/mapCpmFrequencyGraphDetails";
import mapCostFrequencyGraphDetails from "../../../helpers/mapCostFrequencyGraphDetails";
import FrequencyGraph from "./FrequencyGraph";
import PanelStateIndicator from "../PanelStateIndicator";
import Tooltip from "../../Tooltip";
import messages from "../../../helpers/messages";
import InfoIcon from "../../icons/InfoIcon";

export const GraphsSection = ({ currency, division }) => {
  const {
    analytics: {
      loads,
      analyticsGraphType,
      aggregatedForecastPricing,
      pollingLaneForecast
    }
  } = useSelector(state => state);
  const dispatch = useDispatch();

  const graphOptions = getAnalyticsGraphOptions(division);
  const [graphType, setGraphType] = useState(
    analyticsGraphType ??
      graphOptions.find(t => t.value === graphTypesV2.costOverTime)
  );
  const [graphDetails, setGraphDetails] = useState({
    lineGraph: null
  });

  useEffect(() => {
    dispatch(setAnalyticsGraphType(graphType));
  }, [dispatch, graphType]);

  useEffect(() => {
    const includedLoads = loads?.filter(l => !l.exclude);
    setGraphDetails({
      lineGraph: [
        {
          name: lineGraphSeries.historical,
          data: mapLineGraphDetails(orderBy(includedLoads, ["loadDate"]))
        },
        {
          name: lineGraphSeries.forecasted,
          data: mapAggregatedPricingForecastDetails(
            aggregatedForecastPricing ?? []
          )
        }
      ],
      costFrequency: mapCostFrequencyGraphDetails(includedLoads),
      cpmFrequency: mapCpmFrequencyGraphDetails(includedLoads)
    });
  }, [loads, aggregatedForecastPricing]);

  return (
    <>
      {pollingLaneForecast ? (
        <div className="box panel">
          <PanelStateIndicator iconSize="32px" loading={pollingLaneForecast} />
        </div>
      ) : (
        <>
          <div className="one-whole mb-8">
            <div className="chart-heading">
              {graphType.heading}
              {graphType.value === graphTypesV2.costOverTime ||
              graphType.value === graphTypesV2.cpmOverTime ? (
                <Tooltip
                  content={
                    messages.laneForecastEquipTypeInfoTooltip.defaultMessage
                  }
                  placement="top-end"
                  offset={[12, 10]}
                >
                  <span>
                    <InfoIcon
                      height={12}
                      width={12}
                      className="ml-4 align-text-top"
                      fill="#0066FA"
                      title=""
                    />
                  </span>
                </Tooltip>
              ) : null}
            </div>

            <div className="one-sixth p-4 fr pos-absolute chart-type">
              <AutoComplete
                staticOptions={graphOptions}
                value={graphType}
                handleChange={setGraphType}
                backspaceRemovesValue={false}
                isMini={true}
              />
            </div>
          </div>
          {graphType.value === graphTypesV2.costOverTime ||
          graphType.value === graphTypesV2.cpmOverTime ? (
            <LineGraph
              data={graphDetails.lineGraph}
              currency={currency}
              division={division}
              {...graphType}
            />
          ) : graphType.value === graphTypesV2.cpmFrequency ? (
            <FrequencyGraph
              data={graphDetails.cpmFrequency}
              currency={currency}
              division={division}
              showBrush={true}
              {...graphType}
            />
          ) : (
            <FrequencyGraph
              data={graphDetails.costFrequency}
              currency={currency}
              division={division}
              showBrush={true}
              {...graphType}
            />
          )}
        </>
      )}
    </>
  );
};

export default GraphsSection;
